import { useCallback, useEffect, useState } from 'react';
import { Link2, Check, X, Clock, Copy, PackageCheck, Loader2, RefreshCw, Truck, Ban, ExternalLink } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface Submission {
  id: number;
  supplierName?: string;
  contactPhone?: string;
  submissionType: 'new' | 'update';
  name: string;
  description?: string | null;
  category?: string;
  spicinessLevel?: number;
  suggestedPrice: number;
  suggestedOriginalPrice?: number | null;
  suggestedStock?: number;
  imageUrl?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string | null;
  note?: string | null;
  createdAt: number;
}

interface TokenRow {
  id: number;
  token: string;
  supplierName: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  expiresAt: number;
  revokedAt?: number | null;
  isActive: boolean;
  isExpired: boolean;
  isStanding?: boolean | number;
  submissionCount: number;
  pendingCount: number;
  createdAt: number;
}

const fmtHkd = (cents?: number | null) => (cents ? `HK$${(cents / 100).toFixed(2)}` : '—');
const fmtTime = (ts?: number | null) => (ts ? new Date(ts * 1000).toLocaleString('zh-HK') : '—');

export default function AdminSupplier() {
  const [tab, setTab] = useState<'submissions' | 'links'>('submissions');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [subFilter, setSubFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('pending');
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Approve form state
  const [approveTarget, setApproveTarget] = useState<Submission | null>(null);
  const [finalPrice, setFinalPrice] = useState('');
  const [finalStock, setFinalStock] = useState('');

  // Token creation state
  const [supplierName, setSupplierName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [hours, setHours] = useState('48');
  const [standing, setStanding] = useState(false);
  const [newLink, setNewLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Submission | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [subRes, tokenRes] = await Promise.all([
        apiFetch(`/api/public/admin/product-submissions${subFilter ? `?status=${subFilter}` : ''}`),
        apiFetch('/api/public/admin/supplier-tokens'),
      ]);
      const subJson = await subRes.json().catch(() => ({}));
      const tokenJson = await tokenRes.json().catch(() => ({}));
      setSubmissions(Array.isArray(subJson) ? subJson : subJson.data || []);
      const t = Array.isArray(tokenJson) ? tokenJson : tokenJson.data || [];
      setTokens(Array.isArray(t) ? t : []);
    } catch (e) {
      setError('載入失敗，請重新整理');
    } finally {
      setLoading(false);
    }
  }, [subFilter]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (s: Submission) => {
    setApproveTarget(s);
    setFinalPrice((s.suggestedPrice / 100).toFixed(2));
    setFinalStock(String(s.suggestedStock ?? 50));
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setBusyId(approveTarget.id);
    try {
      const res = await apiFetch(`/api/public/admin/product-submissions/${approveTarget.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: Math.round(parseFloat(finalPrice) * 100),
          stockQuantity: parseInt(finalStock, 10) || 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.data || data)?.error) throw new Error((data.data || data)?.error || '審批失敗');
      setApproveTarget(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '審批失敗');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setBusyId(rejectTarget.id);
    try {
      const res = await apiFetch(`/api/public/admin/product-submissions/${rejectTarget.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      if (!res.ok) throw new Error('拒絕失敗');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '拒絕失敗');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateToken = async () => {
    if (!supplierName.trim() || !contactPhone.trim()) {
      setError('請填寫供貨商名稱與聯絡電話');
      return;
    }
    setError('');
    try {
      const res = await apiFetch('/api/public/admin/supplier-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplierName: supplierName.trim(), contactPhone: contactPhone.trim(), hours: parseInt(hours, 10) || 48, standing }),
      });
      const data = await res.json().catch(() => ({}));
      const d = data.data || data;
      if (!res.ok || !d?.url) throw new Error(d?.error || '生成失敗');
      setNewLink(d.url);
      setSupplierName(''); setContactPhone(''); setHours('48'); setStanding(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '生成失敗');
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm('確定取消此連結？供貨商將無法再用它提交。')) return;
    try {
      await apiFetch(`/api/public/admin/supplier-tokens/${id}/revoke`, { method: 'POST' });
      load();
    } catch {
      setError('取消連結失敗');
    }
  };

  const copyLink = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin';
  };

  return (
    <AdminLayout currentPage="supplier" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Truck size={24} className="text-red-600" /> 供貨商中心
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          生成 48 小時提交連結俾供貨商；所有產品先入審批隊列，批准先會上架（products 表只由管理員寫入）
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'submissions', label: `提交審批${submissions.length ? ` (${submissions.length})` : ''}` },
          { key: 'links', label: '供貨商連結' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as 'submissions' | 'links')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-red-600 text-red-600 dark:text-red-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 self-center" title="重新整理">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {tab === 'submissions' && (
        <div>
          <div className="flex gap-2 mb-4">
            {[
              { key: 'pending', label: '待審批' },
              { key: 'approved', label: '已通過' },
              { key: 'rejected', label: '已拒絕' },
              { key: '', label: '全部' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setSubFilter(f.key as typeof subFilter)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  subFilter === f.key
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">載入中...</div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
              <PackageCheck size={40} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              {subFilter === 'pending' ? '冇待審批提交' : '冇記錄'}
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map((s) => (
                <div key={s.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex gap-4">
                  <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    {s.imageUrl ? (
                      <img src={s.imageUrl} alt={s.name} className="w-full h-full object-cover" />
                    ) : (
                      <PackageCheck size={28} className="text-gray-300 dark:text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-900 dark:text-white">{s.name}</span>
                      {s.submissionType === 'update' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">修改現有</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        s.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        s.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>{s.status === 'pending' ? '待審批' : s.status === 'approved' ? '已通過' : '已拒絕'}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {s.supplierName || '未知供貨商'} · 建議價 {fmtHkd(s.suggestedPrice)}
                      {s.suggestedOriginalPrice ? `（原價 ${fmtHkd(s.suggestedOriginalPrice)}）` : ''} · 庫存 {s.suggestedStock ?? 0} · 辣度 {s.spicinessLevel ?? 1}/5
                    </p>
                    {s.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{s.description}</p>}
                    {s.note && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">📝 供貨商備註：{s.note}</p>}
                    {s.status === 'rejected' && s.adminNotes && <p className="text-xs text-red-500 mt-1">拒絕原因：{s.adminNotes}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">{fmtTime(s.createdAt)}</p>
                  </div>
                  {s.status === 'pending' && (
                    <div className="flex flex-col gap-2 shrink-0 self-center">
                      <button onClick={() => openApprove(s)}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 flex items-center gap-1">
                        <Check size={13} /> 批准上架
                      </button>
                      <button onClick={() => setRejectTarget(s)}
                              className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-bold hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-1">
                        <X size={13} /> 拒絕
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'links' && (
        <div className="space-y-6">
          {/* Create */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Link2 size={16} className="text-red-600" /> 生成新連結</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="供貨商名稱 *"
                     className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm" />
              <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="聯絡電話（WhatsApp）*"
                     className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm" />
              <input value={hours} onChange={(e) => setHours(e.target.value)} type="number" min="1" max="720" placeholder="有效時數"
                     disabled={standing}
                     className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm disabled:opacity-40" />
              <button onClick={handleCreateToken}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700">
                生成連結
              </button>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
              <input type="checkbox" checked={standing} onChange={(e) => setStanding(e.target.checked)} className="accent-red-600 w-4 h-4" />
              長期連結（已確認合作關係嘅夥伴；不設時限，可隨時撤銷——對方可收藏作書籤直達供貨商門戶）
            </label>
            {newLink && (
              <div className="mt-4 flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-700 dark:text-gray-300">
                  {newLink}
                </code>
                <button onClick={() => copyLink(newLink)}
                        className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 ${copied ? 'bg-green-600 text-white' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                  {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? '已複製' : '複製'}
                </button>
              </div>
            )}
          </div>

          {/* Token list */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 text-xs">
                <tr>
                  <th className="text-left px-4 py-3">供貨商</th>
                  <th className="text-left px-4 py-3">狀態</th>
                  <th className="text-left px-4 py-3">提交</th>
                  <th className="text-left px-4 py-3">到期</th>
                  <th className="text-right px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tokens.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400">未生成過連結</td></tr>
                )}
                {tokens.map((t) => (
                  <tr key={t.id} className="text-gray-700 dark:text-gray-300">
                    <td className="px-4 py-3">
                      <p className="font-medium">{t.supplierName}</p>
                      <p className="text-xs text-gray-400">{t.contactPhone}</p>
                    </td>
                    <td className="px-4 py-3">
                      {t.isStanding ? (
                        <span className="text-xs font-bold text-green-600">長期</span>
                      ) : t.isActive ? (
                        <span className="text-xs font-bold text-green-600">有效</span>
                      ) : t.revokedAt ? (
                        <span className="text-xs font-bold text-gray-400">已取消</span>
                      ) : (
                        <span className="text-xs font-bold text-gray-400 flex items-center gap-1"><Clock size={11} /> 已過期</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.submissionCount} 筆{t.pendingCount > 0 && <span className="text-amber-600 font-bold">（{t.pendingCount} 待審）</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">{t.isStanding ? '—' : fmtTime(t.expiresAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a href={`/supplier/portal?token=${t.token}`} target="_blank" rel="noopener noreferrer"
                           className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="打開供貨商門戶">
                          <ExternalLink size={14} />
                        </a>
                        {t.isActive && (
                          <button onClick={() => handleRevoke(t.id)} className="text-red-400 hover:text-red-600" title="取消連結">
                            <Ban size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approve modal */}
      {approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">批准上架：{approveTarget.name}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              供貨商建議售價 {fmtHkd(approveTarget.suggestedPrice)} · 你可以調整最終售價與初始庫存
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最終售價（HK$）</label>
                <input type="number" min="1" step="0.1" value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">初始庫存</label>
                <input type="number" min="0" value={finalStock} onChange={(e) => setFinalStock(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setApproveTarget(null)}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm">
                取消
              </button>
              <button onClick={handleApprove} disabled={busyId === approveTarget.id}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                {busyId === approveTarget.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 確認上架
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">拒絕提交：{rejectTarget.name}</h2>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">原因（會經 WhatsApp 發送俾供貨商）</label>
            <textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white resize-none"
                      placeholder="例如：圖片唔清晰，麻煩重新上傳" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm">
                取消
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || busyId === rejectTarget.id}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                {busyId === rejectTarget.id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} 確認拒絕
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
